import { notifications } from '@mantine/notifications';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Endpoints } from '../../apis';
import { getErrorMessage } from '@xver/utils/error';

interface InstanceConsoleMutationVariables {
    id: number;
    consoleWindow?: Window | null;
}

export function useInstanceConsoleMutate() {
    const { t } = useTranslation('notifications');
    return useMutation({
        mutationFn: async ({ id, consoleWindow }: InstanceConsoleMutationVariables) => {
            const data = await Endpoints.v1.Instances.openConsole(id);

            if (!data?.url) {
                consoleWindow?.close();
                throw new Error(t('instance.consoleUrlMissing'));
            }

            if (consoleWindow) {
                consoleWindow.location.href = data.url;
                consoleWindow.focus();
            } else {
                window.open(data.url, '_blank');
            }

            return data;
        },
        onError: (error: any) => {
            notifications.show({
                title: t('instance.consoleFailedTitle'),
                message: getErrorMessage(error),
                icon: <IconExclamationCircle size={32} color={'#eb5757'} />,
            });
        },
    });
}

interface ConsoleTarget {
    id: number;
    image: { os: string };
}

/**
 * Sends one instance to its console. Linux gets the in-app serial terminal page, Windows
 * the remote VNC console — the serial console is a getty, and Windows exposes nothing on
 * ttyS0 without extra SAC setup. Call it straight from a click handler: both paths open
 * their window synchronously so the popup blocker lets them through.
 *
 * Returns true when it started the async VNC request, so a caller that shows a per-row
 * spinner only does so for the path that actually waits on the network.
 */
export function openInstanceConsole(
    instance: ConsoleTarget,
    mutation: ReturnType<typeof useInstanceConsoleMutate>
): boolean {
    if (!instance.image.os.toLowerCase().includes('windows')) {
        // Named, so clicking Console again reuses the tab instead of attaching a second
        // xterm to the same serial port and interleaving both clients' keystrokes.
        window
            .open(`/cloud-compute/${instance.id}/terminal`, `xver-terminal-${instance.id}`)
            ?.focus();
        return false;
    }

    const consoleWindow = window.open('', '_blank');
    mutation.mutate({ id: instance.id, consoleWindow });
    return true;
}
